import type { ExternalICalBlocking_ResourceReservation } from "@@/shared/acquire/resources/resource-reservation";
import { resourceReservationIdZodSchema } from "@@/shared/acquire/resources/resource-reservation-id";
import { resourceTypeMeta } from "@@/shared/acquire/resources/resource-type";
import { iconNameZodString } from "@@/shared/icon-name";
import {
    requiredTranslatableZodSchema,
    translation,
} from "@@/shared/translations/translation";
import { z } from "zod";
import {
    dateRangeFactory,
    dateRangeZodObject,
} from "../../date-time/date-range";
import { timeRangeZodSchema } from "../../date-time/time-range";
import type { AgendaResourceData } from "./agenda-resource-data";
import { agendaResourceDataZodObject } from "./agenda-resource-data";

const agendaItem_ExternalICalOrderDataZodObject = z.object({
    _type: z.literal("AGENDA_ITEM__EXTERNAL_ICAL_ORDER_DATA"),
    reservationId: resourceReservationIdZodSchema,
    reservationDateRange: dateRangeZodObject,
    reservationTime: z.union([timeRangeZodSchema, dateRangeZodObject]),
    resource: agendaResourceDataZodObject,
    /** Total quantity, all quantity type quantities summed up */
    totalQuantity: z.number(),
    resourceQuantity: z.number(),
    /** Quantities divided by quantity type */
    quantities: z.array(
        z.object({
            key: z.string(),
            label: requiredTranslatableZodSchema,
            quantity: z.number(),
            iconName: iconNameZodString,
        }),
    ),
});

type AgendaItem_ExternalICalOrderData = z.infer<
    typeof agendaItem_ExternalICalOrderDataZodObject
>;

const agendaItem_ExternalICalOrderDataFactory = {
    from: (params: {
        reservation: ExternalICalBlocking_ResourceReservation;
        resource: AgendaResourceData;
    }) => {
        const { reservation, resource } = params;
        return {
            _type: "AGENDA_ITEM__EXTERNAL_ICAL_ORDER_DATA",
            reservationId: reservation._id,
            reservationTime: reservation.timeRange,
            reservationDateRange: dateRangeFactory.fromTimeRange({
                timeRange: reservation.timeRange,
            }),
            resource,
            totalQuantity: reservation.quantity,
            resourceQuantity: reservation.quantity,
            quantities: [
                {
                    key: "externalIcalOrder",
                    label: translation({
                        sv: "Antal (iCal)",
                        en: "Quantity (iCal)",
                    }),
                    quantity: reservation.quantity,
                    iconName: resourceTypeMeta[resource.resourceType].iconName,
                },
            ],
        } satisfies AgendaItem_ExternalICalOrderData;
    },
};

export {
    agendaItem_ExternalICalOrderDataFactory,
    agendaItem_ExternalICalOrderDataZodObject,
};
export type { AgendaItem_ExternalICalOrderData };
